import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { ConfirmDialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { landName } from '../KundeDaten';
import { istDrittland } from '../laender-welt';
import { darf, fehlerText } from '../tarifHinweis';
import type { Overview } from '../types';
import { Dateien } from './Dateien';
import { mitGeloeschtText } from './KontaktDaten';
import { LieferantAusgaben } from './LieferantAusgaben';
import { lieferantUrl, type LieferantAntwort } from './LieferantDaten';
import { Personen } from './Personen';
import { SchlagwortChips } from './Schlagwoerter';
import '../kunden.css';
import './kontakte.css';

// IBAN in Vierergruppen zum Lesen
const ibanText = (iban: string) => iban.replace(/(.{4})(?=.)/g, '$1 ');

// Ein Lieferant (/rechnung/lieferanten/:id): Angaben, Ansprechpartner (ohne Kopie), Dateien und seine Ausgaben;
// bearbeiten und entfernen nur mit dem Recht „Kontakte, Leistungen und Lager“. Firma oben gewechselt: zur Liste der
// neuen Firma.
export function LieferantDetail({ data, onChanged }: { data: Overview; onChanged(): Promise<void> }) {
  const { id = '' } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [res, setRes] = useState<LieferantAntwort | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [weg, setWeg] = useState(false);
  const [busy, setBusy] = useState(false);
  // Zahl seiner Ausgaben (aus der Karte): nur dann der Satz im Fenster „entfernen?“
  const [ausgaben, setAusgaben] = useState(0);
  const pflegen = darf(data.tarif, 'kunden');
  const firma = useRef(data.firm.id);

  const laden = useCallback(async () => {
    try {
      setRes(await api.get<LieferantAntwort>(lieferantUrl(id)));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [id]);

  useEffect(() => {
    setRes(null);
    setError(null);
    void laden();
  }, [laden]);

  useEffect(() => {
    if (firma.current !== data.firm.id) navigate('/rechnung/lieferanten', { replace: true });
  }, [data.firm.id, navigate]);

  if (error) {
    return (
      <div className="notice bad">
        {error} <Link to="/rechnung/lieferanten">Zur Lieferantenliste</Link>
      </div>
    );
  }
  if (!res) return <div className="spinner" aria-label="Lädt" />;
  const l = res.lieferant;
  const mitDazu = mitGeloeschtText(res.personen, res.dateien);

  async function entfernen() {
    setBusy(true);
    try {
      await api.del(lieferantUrl(l.id));
      await onChanged();
      toast(`Lieferant „${l.name}“ entfernt`);
      navigate('/rechnung/lieferanten', { replace: true });
    } catch (err) {
      const t = fehlerText(err);
      if (t) toast(t);
      setBusy(false);
      setWeg(false);
    }
  }

  async function kopieren(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast('IBAN kopiert');
    } catch {
      toast('Kopieren geht hier nicht. Bitte die IBAN markieren.');
    }
  }

  // Ansprechpartner oder Dateien geändert: Lieferant neu (und die Liste, Zahl der Dateien)
  const geaendert = async () => {
    await Promise.all([laden(), onChanged()]);
  };

  return (
    <>
      <Link to="/rechnung/lieferanten" className="kd-back">
        ← Alle Lieferanten
      </Link>
      <div className="rt-head kd-head">
        <div className="kd-title">
          <h1>{l.name}</h1>
          <p className="muted">
            Lieferantennummer <span className="num">{l.nummer}</span>
            {l.country !== 'DE' ? ` · ${landName(l.country)}` : ''}
          </p>
          <SchlagwortChips woerter={l.schlagwoerter} zeige={10} />
        </div>
        {pflegen && (
          <div className="rt-head-act">
            <Link to="bearbeiten" className="btn">
              Bearbeiten
            </Link>
          </div>
        )}
      </div>

      <div className="kd-cols">
        <section className="rt-card kd-angaben" aria-labelledby="kt-angaben-h">
          <h2 id="kt-angaben-h">Angaben</h2>
          <dl>
            <Zeile titel="Anschrift">
              <span className="kd-adr">
                {[l.name, l.extra, l.street, `${l.zip} ${l.city}`.trim(), l.country !== 'DE' ? landName(l.country) : null]
                  .filter(Boolean)
                  .join('\n')}
              </span>
            </Zeile>
            {l.email && (
              <Zeile titel="E-Mail">
                <a href={`mailto:${l.email}`}>{l.email}</a>
              </Zeile>
            )}
            {l.phone && (
              <Zeile titel="Telefon">
                <a href={`tel:${l.phone.replace(/[^\d+]/g, '')}`}>{l.phone}</a>
              </Zeile>
            )}
            {l.vatId && (
              <Zeile titel={istDrittland(l.country) ? 'Steuernummer' : 'USt-IdNr.'}>
                <span className="num">{l.vatId}</span>
              </Zeile>
            )}
            {l.iban && (
              <Zeile titel="IBAN">
                <span className="kt-iban">
                  <span className="num">{ibanText(l.iban)}</span>
                  <button type="button" className="btn small" onClick={() => void kopieren(l.iban!)}>
                    Kopieren
                  </button>
                </span>
              </Zeile>
            )}
            {l.kundennummerDort && (
              <Zeile titel="Unsere Kundennr.">
                <span className="num">{l.kundennummerDort}</span>
              </Zeile>
            )}
            {l.notiz && (
              <Zeile titel="Notiz">
                <span className="kd-notiz">{l.notiz}</span>
              </Zeile>
            )}
          </dl>
        </section>
        <div className="kt-spalte">
          <Personen art="lieferant" kontaktId={l.id} personen={res.personen} pflegen={pflegen} onChanged={geaendert} />
          <Dateien
            art="lieferant"
            kontaktId={l.id}
            dateien={res.dateien}
            maxMb={res.dateienMb}
            tarif={data.tarif}
            pflegen={pflegen}
            onChanged={geaendert}
          />
          <LieferantAusgaben data={data} lieferantId={l.id} onAnzahl={setAusgaben} />
        </div>
      </div>

      {pflegen && (
        <p className="kd-remove">
          <button type="button" className="linkbtn rt-danger" onClick={() => setWeg(true)}>
            Lieferant entfernen
          </button>
        </p>
      )}
      <ConfirmDialog
        open={weg}
        title={`Lieferant „${l.name}“ entfernen?`}
        text={[mitDazu || 'Er verschwindet aus der Lieferantenliste.', ausgaben ? 'Ausgaben behalten den Namen, wie er dort steht.' : '']
          .filter(Boolean)
          .join(' ')}
        confirmLabel="Ja, entfernen"
        danger
        busy={busy}
        onConfirm={() => void entfernen()}
        onCancel={() => setWeg(false)}
      />
    </>
  );
}

function Zeile({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div>
      <dt>{titel}</dt>
      <dd>{children}</dd>
    </div>
  );
}
